import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import type { FC } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';

import EditIcon from '@material-ui/icons/Edit';
import { BrandsProducts } from 'src/interfaces/new-channel-partner/channelPartner';
import { AddBrandsProducts } from 'src/components/common/ProductBrandSection/addBrandsProducts';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { useDialog } from 'src/hooks/use-dialog';
import { ChevronLeft as ChevronLeftIcon } from '../../../icons/chevron-left';
import { ChevronDown as ChevronDownIcon } from '../../../icons/chevron-down';
import { Trash as TrashIcon } from '../../../icons/trash';

interface BrandsProductsProps {
  brandsProducts: BrandsProducts[];
  cpBrandsProducts?: BrandsProducts[];
  scope?: string;
  addBrand?: (data: BrandsProducts) => Promise<boolean>;
  updateBrand?: (data: BrandsProducts) => Promise<boolean>;
  deleteBrand: (data: string) => void;
}

export const ShowBrandsProductsCard: FC<BrandsProductsProps> = (props) => {
  const { brandsProducts, cpBrandsProducts, scope, addBrand, updateBrand, deleteBrand } = props;
  const [serviceToBeDeleted, setServiceToBeDeleted] = useState<BrandsProducts>();
  const [productToBeEdit, setProductToBeEdit] = useState<BrandsProducts>();
  const [displayInfo, setDisplayInfo] = useState<boolean>(true);
  const [openAddBrandDialog, setOpenAddBrandDialog] = useState<boolean>(false);
  const [isAdd, setIsAdd] = useState<boolean>(false);

  const serviceToBeDeleteDetails = (brand: BrandsProducts) => {
    setServiceToBeDeleted(brand);
  };
  const [
    deleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog
  ] = useDialog();

  useEffect(() => {
    if (serviceToBeDeleted) {
      handleOpenDeleteDialog();
    }
  }, [serviceToBeDeleted]);

  const handleDeleteService = async () => {
    handleCloseDeleteDialog();
    await deleteBrand(serviceToBeDeleted.brandId);
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mt: 1
        }}
      >
        <CardHeader
          titleTypographyProps={{ variant: 'h2' }}
          title="Product & Brands"
          action={(
            <>
              <Button
                color="primary"
                size="small"
                disabled={cpBrandsProducts && cpBrandsProducts.length === brandsProducts.length}
                onClick={() => {
                  setIsAdd(true);
                  setOpenAddBrandDialog(true);
                }}
                variant="contained"
              >
                Add
              </Button>
              { brandsProducts?.length > 0 && (
                <>
                  { displayInfo && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayInfo(false);
                    }}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                  )}
                  { !displayInfo && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayInfo(true);
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  )}
                </ >
              )}
            </>
          )}
        />
        {displayInfo && (
          <>
            {brandsProducts?.length > 0 && (
            <CardContent>
              <Grid
                container
                spacing={1}
              >
                <Grid
                  item
                  md={12}
                  xs={12}
                  sm={12}
                >
                  <Card
                    variant="outlined"
                  >
                    <TableContainer
                      sx={{
                        maxHeight: '220px',
                      }}
                    >
                      <Table
                        size="small"
                        stickyHeader
                        aria-label="sticky table"
                      >
                        <TableHead>
                          <TableRow
                            key="brandTable"
                          >
                            <TableCell
                              width="10%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Brand
                            </TableCell>
                            <TableCell
                              width="75%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Products
                            </TableCell>
                            <TableCell
                              align="center"
                              width="15%"
                              sx={{
                                backgroundColor: 'neutral.100',
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {brandsProducts?.map((item) => (
                            <TableRow
                              key={item.brandId}
                            >
                              <TableCell>
                                {item.name}
                              </TableCell>
                              <TableCell>
                                {item.products?.map((products, index) => (
                                  (index !== item.products.length - 1) ? `${products.name}, ` : `${products.name}`
                                ))}
                              </TableCell>
                              <TableCell
                                align="center"
                              >
                                <Box>
                                  <IconButton
                                    title="Edit Products of this Brand"
                                    aria-label="edit"
                                    onClick={() => {
                                      setProductToBeEdit(item);
                                      setIsAdd(false);
                                      setOpenAddBrandDialog(true);
                                    }}
                                    style={{ padding: 0, marginRight: 2 }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  {' '}
                                  |
                                  {' '}
                                  <IconButton
                                    title="Delete this Brand and its product"
                                    color="primary"
                                    onClick={() => {
                                      serviceToBeDeleteDetails(item);
                                    }}
                                    type="button"
                                    style={{ padding: 0 }}
                                  >
                                    <TrashIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                    </TableContainer>

                  </Card>
                </Grid>
              </Grid>
            </CardContent>
            )}
          </ >
        )}
      </Card>
      {openAddBrandDialog && (
      <AddBrandsProducts
        brandsProducts={isAdd ? brandsProducts : [productToBeEdit]}
        isAdd={isAdd}
        cpBrandsProducts={cpBrandsProducts}
        scope={scope}
        open={openAddBrandDialog}
        addBrand={addBrand}
        updateBrand={updateBrand}
        onClose={() => setOpenAddBrandDialog(false)}
      />
      )}
      <ConfirmationDialog
        message="Are you really want to delete the Brand?"
        onCancel={() => {
          handleCloseDeleteDialog();
        }}
        onConfirm={handleDeleteService}
        open={deleteDialogOpen}
        title="Are You Sure?"
        variant="error"
      />
    </>
  );
};

ShowBrandsProductsCard.propTypes = {
  brandsProducts: PropTypes.array,
  cpBrandsProducts: PropTypes.array,
  scope: PropTypes.string,
  addBrand: PropTypes.func,
  updateBrand: PropTypes.func,
  deleteBrand: PropTypes.func
};
